import { useMemo, useRef, useState } from "react";
import { BackupBar } from "@/components/BackupBar";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Download, Share2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { listToPagedPngBlobs, pngBlobsToLetterPdfBytes } from "@/lib/printExport";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { addDays, dayDiff, parseLocalDate, toIsoLocal, useStored } from "../travel/tripStore";
import { useExportGate } from "@/hooks/useExportGate";
import { useShareGate } from "@/hooks/useShareGate";
import { ExportUsageNote } from "@/components/ExportUsageNote";

const CHECKLIST_TEMPLATE: { task: string; weeksBefore: number }[] = [
  {
    task: "Research neighborhoods / sign lease or close on home",
    weeksBefore: 8,
  },
  { task: "Get moving quotes / book movers", weeksBefore: 6 },
  { task: "Start decluttering and donating", weeksBefore: 6 },
  { task: "Notify landlord (if renting)", weeksBefore: 5 },
  { task: "Research and enroll kids in new school", weeksBefore: 5 },
  { task: "Order packing supplies", weeksBefore: 4 },
  { task: "Transfer or set up utilities at new address", weeksBefore: 3 },
  { task: "Update address with USPS, bank, subscriptions", weeksBefore: 3 },
  { task: "Transfer medical/vet records", weeksBefore: 2 },
  { task: "Start packing non-essentials", weeksBefore: 3 },
  { task: "Confirm moving day details with movers", weeksBefore: 1 },
  {
    task: "Pack essentials box (documents, medications, chargers)",
    weeksBefore: 0.5,
  },
  { task: "Do final walkthrough of old place", weeksBefore: 0.1 },
];

interface ChecklistItem {
  id: number;
  task: string;
  dueDate: string; // ISO yyyy-mm-dd
  checked: boolean;
}

let nextItemId = 1;

function buildFromMoveDate(moveDate: string): ChecklistItem[] {
  const day = parseLocalDate(moveDate) ?? new Date();
  return CHECKLIST_TEMPLATE.map(t => {
    const due = addDays(day, -Math.round(t.weeksBefore * 7));
    return {
      id: nextItemId++,
      task: t.task,
      dueDate: toIsoLocal(due),
      checked: false,
    };
  }).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

const TOOL_INFO_CONTENT = {
  what: `Provides a relocation checklist that counts down to your move date, covering lease notices, utility transfers, address changes, and school enrollment. Every task's date and text can be edited, and you can add your own entries.`,
  how: `Enter your move date to generate a starting checklist, then edit any task's text or date, add your own entries, and check items off as you complete them.`,
  tip: `The address-change and utility-transfer tasks are the easiest ones to forget until the last minute — starting those a few weeks earlier than feels necessary avoids a stressful gap in service right after you move.`,
};

export default function RelocationChecklistPlanner() {
  const [moveDate, setMoveDate] = useStored("fdp-move-date-v1", toIsoLocal(addDays(new Date(), 56)));
  const [items, setItems] = useStored<ChecklistItem[]>("fdp-move-checklist-v1", [], );
  // First visit: build the starting list. Later visits: the saved list (ticks, edits and all) is used as is.
  if (items.length === 0 && !sessionStorage.getItem("fdp-move-checklist-v1")) setItems(buildFromMoveDate(moveDate));
  nextItemId = Math.max(nextItemId, items.reduce((m, i) => Math.max(m, i.id), 0) + 1);
  const listRef = useRef<HTMLDivElement>(null);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [items]
  );

  const toggle = (id: number) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, checked: !i.checked } : i)));
  };
  const updateTask = (id: number, task: string) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, task } : i)));
  };
  const updateDate = (id: number, dueDate: string) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, dueDate } : i)));
  };
  const removeItem = (id: number) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };
  const addItem = () => {
    setItems(prev => [
      ...prev,
      { id: nextItemId++, task: "New task", dueDate: moveDate, checked: false },
    ]);
  };
  const regenerateFromDate = () => {
    setItems(buildFromMoveDate(moveDate));
    toast.success("Checklist regenerated from your move date — any custom edits were reset.");
  };

  const { checkExport } = useExportGate();
  const { gateShare } = useShareGate();

  // A long checklist is cut between tasks into Letter-proportioned parts instead of one very tall picture.
  const renderParts = async () => {
    const rows = Array.from(listRef.current!.children) as HTMLElement[];
    return listToPagedPngBlobs(listRef.current!, rows, { pixelRatio: 2, backgroundColor: "#ffffff" });
  };

  const downloadAsImage = async () => {
    if (!listRef.current) return;
    if (!(await checkExport())) return;
    try {
      const parts = await renderParts();
      for (let i = 0; i < parts.length; i++) {
        downloadBlob(parts[i], parts.length > 1 ? `relocation-checklist-part-${i + 1}-of-${parts.length}.png` : "relocation-checklist.png");
        if (i < parts.length - 1) await new Promise((r) => setTimeout(r, 400));
      }
      toast.success(parts.length > 1 ? `Checklist downloaded as ${parts.length} images (a long list is split between tasks).` : "Checklist downloaded as image!");
    } catch (err) {
      console.error("Failed to export checklist as image:", err);
      toast.error("Couldn't export the image right now.");
    }
  };

  const downloadAsPdf = async () => {
    if (!listRef.current) return;
    if (!(await checkExport())) return;
    try {
      const bytes = await pngBlobsToLetterPdfBytes(await renderParts());
      downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), "relocation-checklist.pdf");
      toast.success("Checklist downloaded as PDF!");
    } catch (err) {
      console.error("Failed to export checklist as PDF:", err);
      toast.error("Couldn't build the PDF right now.");
    }
  };

  const shareChecklist = async () => {
    const summary =
      `Relocation Checklist (move date: ${moveDate})\n\n` +
      sortedItems
        .map(i => `${i.checked ? "[x]" : "[ ]"} ${i.dueDate} — ${i.task}`)
        .join("\n") +
      "\n\nMade with FreeDailyPro's Relocation Checklist Planner";
    // Relocation Checklist Planner is a Premium Application: Share is a way the result leaves the page, so it goes
    // through the export gate (paywall and nothing shared at 0 remaining; a use is taken only when shared).
    await gateShare(async () => {
      if (navigator.share) {
        try {
          await navigator.share({ title: "Relocation Checklist", text: summary });
          return true;
        } catch {
          return false; // user cancelled the native share sheet: not an error, and not a use
        }
      }
      try {
        await navigator.clipboard.writeText(summary);
        toast.success("Checklist copied to clipboard!");
        return true;
      } catch {
        toast.error("Couldn't copy. Select the checklist and copy it by hand.");
        return false;
      }
    });
  };

  return (
    <ToolContainer>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <Label className="mb-2 block">Move Date</Label>
          <Input aria-label="Move Date"
            type="date"
            value={moveDate}
            onChange={e => setMoveDate(e.target.value)}
            className="max-w-xs"
          />
        </div>
        <Button variant="outline" size="sm" onClick={regenerateFromDate} className="gap-1.5">
          <RotateCcw className="h-3.5 w-3.5" /> Regenerate from Move Date
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-3">
        {items.filter(i => i.checked).length} of {items.length} done · {items.filter(i => !i.checked && parseLocalDate(i.dueDate) && dayDiff(new Date(), parseLocalDate(i.dueDate)!) < 0).length} overdue · kept for this visit only
      </p>

      <div ref={listRef} className="space-y-2 bg-background p-1">
        {sortedItems.map(item => (
          <Card
            key={item.id}
            className={`p-3 flex items-center gap-3 ${
              !item.checked && (parseLocalDate(item.dueDate) ? dayDiff(new Date(), parseLocalDate(item.dueDate)!) < 0 : false) ? "border-red-300 bg-red-50 dark:bg-red-950/20" : item.checked ? "opacity-60" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={item.checked}
              onChange={() => toggle(item.id)}
              className="shrink-0"
            />
            <Input
              value={item.task}
              onChange={e => updateTask(item.id, e.target.value)}
              className={`flex-1 border-none shadow-none px-1 h-auto py-1 text-sm ${
                item.checked ? "line-through text-muted-foreground" : ""
              }`}
            />
            <Input
              type="date"
              value={item.dueDate}
              onChange={e => updateDate(item.id, e.target.value)}
              className="w-[150px] shrink-0 text-xs h-8"
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={() => removeItem(item.id)}
              className="shrink-0 h-8 w-8"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button variant="outline" onClick={addItem} className="gap-1.5">
          <Plus className="h-4 w-4" /> Add Task
        </Button>
        <Button variant="outline" onClick={downloadAsImage} className="gap-1.5">
          <Download className="h-4 w-4" /> Download as Image
        </Button>
        <Button variant="outline" onClick={downloadAsPdf} className="gap-1.5">
          <Download className="h-4 w-4" /> Download as PDF
        </Button>
        <Button variant="outline" onClick={shareChecklist} className="gap-1.5">
          <Share2 className="h-4 w-4" /> Share
        </Button>
        <ExportUsageNote className="basis-full" />
      </div>

          <BackupBar tool="relocation-checklist-planner" prefixes={["fdp-move-"]} session />

          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
